import { first } from 'lodash';

import { PipelineEditorError } from '../edit-pipeline/errors';
import {
  Aggregation,
  AggregationExtendedType,
  AggregationType,
  Field,
  Fields,
  FilterCondition,
  FilteringAggregation,
  Metric,
  Property,
  aggregationZod,
} from '../types';
import { ensureUniqueFieldKey, nameToKey } from '../edit-pipeline/utils';
import { createFieldOption } from '../components/select-options/field';
import {
  isExprAggregation,
  isKeyedAggregation,
  isMetricAggregation,
  isSortedAggregation,
} from '../pipeline/operation';
import { isDateField } from '../components/visualisation/utils';
import { FieldOrGroup, flattenFieldGroups, getUniqueFieldName } from '../pipeline/utils';

export interface AggregationOption {
  label: string;
  value: AggregationExtendedType;
  kind: 'key' | 'empty' | 'expr';
  fieldTypes?: string[];
}

export const AggregationOptions: AggregationOption[] = [
  { label: 'Count', value: 'count', kind: 'empty' },
  { label: 'Sum', value: 'sum', kind: 'key', fieldTypes: ['Number', 'Integer', 'Float'] },
  { label: 'Count Distinct', value: 'count_distinct', kind: 'key' },
  { label: 'Minimum', value: 'min', kind: 'key', fieldTypes: ['Number', 'Integer', 'Float'] },
  { label: 'Maximum', value: 'max', kind: 'key', fieldTypes: ['Number', 'Integer', 'Float'] },
  { label: 'Earliest', value: 'earliest', kind: 'key', fieldTypes: ['Date'] },
  { label: 'Latest', value: 'latest', kind: 'key', fieldTypes: ['Date'] },
  { label: 'First Value', value: 'first', kind: 'key' },
  { label: 'Average', value: 'avg', kind: 'key', fieldTypes: ['Number', 'Integer', 'Float'] },
  { label: 'Median', value: 'median', kind: 'key', fieldTypes: ['Number', 'Integer', 'Float'] },
  { label: 'List of', value: 'array', kind: 'key' },
];

const getAggregationOptionByType = (type: AggregationExtendedType) =>
  AggregationOptions.find((option) => option.value === type);

export const getAggregationOptionLabelByType = (type: AggregationExtendedType) =>
  getAggregationOptionByType(type)?.label;

const getFieldsForAggregationType = (type: AggregationExtendedType, fields: Fields) => {
  const aggregationOption = AggregationOptions.find((a) => a.value === type);

  return aggregationOption === undefined
    ? []
    : fields.filter(
        ({ type }) => type === null || (aggregationOption.fieldTypes?.includes(type ?? '') ?? true),
      );
};

export const getFieldsForAggregation = (aggregation: Aggregation, fields: Fields) =>
  getFieldsForAggregationType(
    aggregationTypeToAggregationExtendedType(
      aggregation.type,
      isKeyedAggregation(aggregation) ? fields.find((f) => f.key === aggregation.key) : undefined,
    ),
    fields,
  );

export const getFieldOptionsForAggregationType = (type: AggregationExtendedType, fields: Fields) =>
  getFieldsForAggregationType(type, fields).map(createFieldOption);

export const getAggregationOptionsForFields = (
  fields: FieldOrGroup[],
  aggregationOptions = AggregationOptions,
) => {
  const flattenedFields = flattenFieldGroups(fields);
  return aggregationOptions.filter(({ fieldTypes }) =>
    flattenedFields.some(({ type }) => fieldTypes?.includes(type ?? '') ?? true),
  );
};

/**
 * Convert a normal aggregation type to an extended aggregation type for display.
 * The extended type includes types like `earlier` and `latest` that are converted back
 * into `min` and `max` for the backend.
 * Whether a type `min` should be `min` or `earlier` is determined by the type of the
 * field referenced by the aggregation.
 */
export const aggregationTypeToAggregationExtendedType = (
  aggregationType: AggregationType,
  field?: Field,
) =>
  AggregationOptions.find(
    (option) =>
      aggregationExtendedTypeToAggregationType(option.value) === aggregationType &&
      (option.fieldTypes === undefined || option.fieldTypes.includes(field?.type ?? '')),
  )?.value ?? aggregationType;

/**
 * Convert an extended aggregation type to a normal aggregation type for backend.
 */
export const aggregationExtendedTypeToAggregationType = (
  extendedType: AggregationExtendedType,
): AggregationType => {
  switch (extendedType) {
    case 'earliest':
      return 'min';
    case 'latest':
      return 'max';
    default:
      return extendedType;
  }
};

export const getAggregationLabel = (type: AggregationExtendedType, field?: Field) => {
  const fieldName = field?.name ?? 'Unknown';
  switch (type) {
    case 'count':
      return 'Count';
    case 'count_distinct':
      return `Count of ${fieldName} (distinct)`;
    case 'sum':
      return `Sum of ${fieldName}`;
    case 'min':
      return `Min ${fieldName}`;
    case 'max':
      return `Max ${fieldName}`;
    case 'earliest':
      return `Earliest ${fieldName}`;
    case 'latest':
      return `Latest ${fieldName}`;
    case 'avg':
      return `Average ${fieldName}`;
    case 'median':
      return `Median ${fieldName}`;
    case 'first':
      return `First Value of ${fieldName}`;
    case 'last':
      return `Last Value of ${fieldName}`;
    case 'array':
      return `List of ${fieldName}`;
    case 'custom':
      return 'Custom Formula';
    default:
      return 'Unknown Value';
  }
};

const generateMetricAggregationProperty = (metric: Metric, fields: Field[]) => {
  return {
    key: ensureUniqueFieldKey(fields, metric.metricId),
    name: getUniqueFieldName(fields, metric.name),
  };
};

const generateKeyedAggregationProperty = (
  aggregationType: AggregationType,
  field: Field,
  fieldNameSuffix: string | undefined,
  existingFields: Field[],
) => {
  return {
    key: ensureUniqueFieldKey(existingFields, `${aggregationType}_${field.key}`),
    name: getUniqueFieldName(
      existingFields,
      `${getAggregationLabel(aggregationType, field)}${
        fieldNameSuffix !== undefined ? ` (${fieldNameSuffix})` : ''
      }`,
    ),
  };
};

const generateExprAggregationProperty = (fields: Field[]) => {
  return {
    key: ensureUniqueFieldKey(fields, 'custom'),
    name: getUniqueFieldName(fields, 'Custom Formula'),
  };
};

const generateCountAggregationProperty = (
  relatedFieldSuffix: string | undefined,
  fields: Field[],
) => {
  if (relatedFieldSuffix !== undefined) {
    return {
      key: ensureUniqueFieldKey(fields, `count_${nameToKey(relatedFieldSuffix)}`),
      name: getUniqueFieldName(fields, `Number of ${relatedFieldSuffix}`),
    };
  }

  return {
    key: ensureUniqueFieldKey(fields, 'count'),
    name: getUniqueFieldName(fields, 'Count'),
  };
};

export const generateAggregationProperty = (
  aggregation: Aggregation,
  existingFields: Field[],
  aggregationFields: Field[],
  metrics: Metric[],
  fieldNameSuffix?: string,
): Property => {
  if (isKeyedAggregation(aggregation)) {
    const field = aggregationFields.find((f) => f.key === aggregation.key);
    if (field === undefined) {
      throw new PipelineEditorError(`Aggregation references unknown field '${aggregation.key}'`);
    }
    return generateKeyedAggregationProperty(
      aggregation.type,
      field,
      fieldNameSuffix,
      existingFields,
    );
  }

  if (isMetricAggregation(aggregation)) {
    const metric = metrics.find((m) => m.metricId === aggregation.metricId);
    if (metric === undefined) {
      throw new PipelineEditorError(
        `Aggregation references unknown metric ${aggregation.metricId}`,
      );
    }
    return generateMetricAggregationProperty(metric, existingFields);
  }

  if (isExprAggregation(aggregation)) {
    return generateExprAggregationProperty(existingFields);
  }

  return generateCountAggregationProperty(fieldNameSuffix, existingFields);
};

const getNextAggregationType = (
  fields: Fields,
  allowedAggregationOptions = AggregationOptions,
): AggregationExtendedType => {
  const aggregationOptions = getAggregationOptionsForFields(fields, allowedAggregationOptions);
  if (fields.length === 0) {
    throw new PipelineEditorError('No fields available for aggregation');
  }

  const aggregationOption =
    aggregationOptions.find(({ fieldTypes }) =>
      fields.some(
        (field) =>
          fieldTypes === undefined ||
          (field.type !== 'Date' && fieldTypes.includes(field.type ?? '')),
      ),
    ) ?? first(aggregationOptions);

  return aggregationOption?.value ?? 'count';
};

export const getDefaultAggregation = (
  existingFields: Fields,
  aggregationFields: Fields,
  metrics: Metric[],
  fieldNameSuffix?: string,
  allowedAggregationOptions = AggregationOptions,
): Aggregation => {
  const type = getNextAggregationType(aggregationFields, allowedAggregationOptions);

  if (type === 'count') {
    return {
      type: 'count',
      property: generateCountAggregationProperty(fieldNameSuffix, existingFields),
    };
  }

  if (type === 'metric') {
    const firstMetric = first(metrics);
    if (firstMetric === undefined) {
      throw new PipelineEditorError('No metrics available for aggregation');
    }
    return {
      type: 'metric',
      metricId: firstMetric.metricId,
      property: generateMetricAggregationProperty(firstMetric, existingFields),
    };
  }

  if (type === 'custom') {
    return {
      type: 'custom',
      value: { expression: '', version: '1' },
      property: generateExprAggregationProperty(existingFields),
    };
  }

  if (type === 'earliest' || type === 'latest') {
    const firstField = aggregationFields.find(isDateField) ?? first(aggregationFields);
    if (firstField === undefined) {
      throw new PipelineEditorError('No date fields available for aggregation');
    }

    const convertedType = type === 'earliest' ? 'min' : 'max';

    return {
      type: convertedType,
      key: firstField.key,
      property: generateKeyedAggregationProperty(
        convertedType,
        firstField,
        fieldNameSuffix,
        existingFields,
      ),
    };
  }

  if (type === 'first' || type === 'last') {
    const firstField = first(aggregationFields);
    if (firstField === undefined) {
      throw new PipelineEditorError('No fields available for aggregation');
    }

    return {
      type,
      key: firstField.key,
      sort: [
        {
          key: firstField.key,
          direction: 'ASC',
        },
      ],
      property: generateKeyedAggregationProperty(
        type,
        firstField,
        fieldNameSuffix,
        aggregationFields,
      ),
    };
  }

  const firstField = first(getFieldsForAggregationType(type, aggregationFields));
  if (firstField === undefined) {
    throw new PipelineEditorError('No fields available for aggregation');
  }

  return {
    type,
    key: firstField.key,
    property: generateKeyedAggregationProperty(type, firstField, fieldNameSuffix, existingFields),
  };
};

export const ensureValidAggregation = (
  aggregation: Aggregation,
  existingFields: Field[],
  aggregationFields: Field[],
  metrics: Metric[],
  relationName?: string,
): Aggregation => {
  const validation = aggregationZod.safeParse(aggregation);

  if (!validation.success) {
    return getDefaultAggregation(existingFields, aggregationFields, metrics, relationName);
  }

  aggregation = validation.data;

  if (aggregation.type === 'count') {
    return {
      type: 'count',
      property: aggregation.property,
    };
  }

  if (aggregation.type === 'custom') {
    return {
      type: 'custom',
      value: aggregation.value,
      property: aggregation.property,
    };
  }

  if (aggregation.type === 'metric') {
    const metric = metrics.find((metric) => metric.metricId === aggregation.metricId);
    return metric
      ? aggregation
      : getDefaultAggregation(existingFields, aggregationFields, metrics, relationName);
  }

  const defaultTargetField = first(getFieldsForAggregation(aggregation, aggregationFields));
  if (defaultTargetField === undefined) {
    return getDefaultAggregation(existingFields, aggregationFields, metrics, relationName);
  }

  if (aggregation.key === undefined) {
    return {
      ...aggregation,
      key: defaultTargetField.key,
    };
  }

  const field = aggregationFields.find((f) => f.key === aggregation.key);
  if (!field) {
    return {
      ...aggregation,
      key: defaultTargetField.key,
    };
  }

  return aggregation;
};

export const ensureValidAggregations = (
  aggregations: Aggregation[],
  existingFields: Field[],
  aggregationFields: Field[],
  metrics: Metric[],
): Aggregation[] => {
  return aggregations.map((aggregation) =>
    ensureValidAggregation(
      aggregation,
      [
        ...existingFields,
        ...aggregations.map((agg) => ({
          key: agg.property.key,
          name: agg.property.name,
          type: null,
        })),
      ],
      aggregationFields,
      metrics,
    ),
  );
};

export const changeAggregationType = (
  aggregation: Aggregation,
  newExtendedType: AggregationExtendedType,
  aggregationFields: Field[],
): Aggregation => {
  const newType = aggregationExtendedTypeToAggregationType(newExtendedType);
  if (newType === 'metric') {
    return aggregation;
  }

  if (newType === 'custom') {
    return {
      type: newType,
      value: { expression: '', version: '1' },
      property: aggregation.property,
    };
  }

  if (newType === 'count') {
    return {
      type: newType,
      property: aggregation.property,
    };
  }

  const validFields = getFieldsForAggregationType(newExtendedType, aggregationFields);
  const prevField = isKeyedAggregation(aggregation)
    ? aggregationFields.find((f) => f.key === aggregation.key)
    : undefined;
  const field = validFields.find((f) => f.key === prevField?.key) ?? first(validFields);

  if (field === undefined) {
    return aggregation;
  }

  if (newType === 'first' || newType === 'last') {
    const defaultSortField =
      aggregationFields.find(({ type }) => type === 'Date') ?? first(aggregationFields);
    const sort = isSortedAggregation(aggregation)
      ? aggregation.sort
      : [
          {
            key: defaultSortField?.key ?? '',
            direction: 'ASC' as const,
          },
        ];
    return {
      type: newType,
      key: field.key,
      property: aggregation.property,
      sort,
    };
  }

  return {
    type: newType,
    key: field.key,
    property: aggregation.property,
  };
};

export const buildMetricAggregation = (metric: Metric): Aggregation => ({
  type: 'metric',
  property: { key: nameToKey(metric.name), name: metric.name },
  metricId: metric.metricId,
});

export const buildDefaultAggregationFilter = (
  aggregation: FilteringAggregation,
  fields: FieldOrGroup[],
): FilterCondition => ({
  key: 'key' in aggregation ? aggregation.key : (flattenFieldGroups(fields).at(0)?.key ?? ''),
  operator: '==',
  value: '',
});
